import React, { ReactNode, useState } from 'react';

import cn from 'classnames';
import { filter, indexOf, lowerCase, noop, reduce, union, uniq } from 'lodash';

import type { CounselorMeritBadge } from '@appTypes/esb';
import Member from '@modules/messaging/components/Member';
import type { CounseledYouthRecipient } from '@modules/messaging/types';
import { Checkbox, SearchInput } from '@shared/components';
import { intl } from '@shared/localization';

import styles from './MBCYouthsMembersList.less';
import { MeritBadgeDropdown } from './MeritBadgesFilterDropdown';

type MBCYouthsMembersListProps = {
  search: string;
  prefix: string;
  recipients: number[];
  counselingBadges: CounselorMeritBadge[];
  list: CounseledYouthRecipient[];
  setSearch: (value: string) => void;
  onRecipientsUpdate: (memberIds: number[]) => void;
};

const MBCYouthsMembersList: React.FC<MBCYouthsMembersListProps> = ({
  search,
  prefix,
  recipients,
  counselingBadges,
  list,
  setSearch,
  onRecipientsUpdate,
}) => {
  const [selectedMeritBadges, setSelectedMeritBadges] = useState<Set<number>>(
    new Set(counselingBadges.map(el => el.meritBadgeId)),
  );

  const onMemberSelect = (members: number[]) => {
    onRecipientsUpdate(uniq(union(recipients, members)));
  };

  const onMemberDeselect = (members: number[]) => {
    onRecipientsUpdate(
      filter(recipients, activeMember => indexOf(members, activeMember) === -1),
    );
  };

  const recipientList = reduce(
    list,
    (memo: ReactNode[], recipient) => {
      const relationships = reduce(
        recipient.relationships,
        (submemo: ReactNode[], relationship) => {
          const isRelationshipSelected =
            indexOf(recipients, relationship.memberId) !== -1;

          submemo.push(
            <div
              key={`relationship-${prefix}-${recipient.memberId}-${relationship.memberId}`}
              className={cn(styles.member, styles.indent)}
            >
              <Checkbox checked={isRelationshipSelected} onClick={() => noop}>
                <Member recipient={relationship} />
              </Checkbox>
            </div>,
          );
          return submemo;
        },
        [],
      );

      const isSelected = indexOf(recipients, recipient.memberId) !== -1;

      const name = `${recipient.lastName}, ${recipient.firstName}`;

      if (search !== '' && !lowerCase(name).includes(lowerCase(search))) {
        return memo;
      }

      if (
        ![...selectedMeritBadges].some(item =>
          recipient.meritBadgesCounseling.has(item),
        )
      ) {
        return memo;
      }

      memo.push(
        <div
          key={`recipient-${prefix}-${recipient.memberId}`}
          className={styles.member}
        >
          <Checkbox
            checked={isSelected}
            onChange={() =>
              isSelected
                ? onMemberDeselect([
                    recipient.memberId,
                    ...recipient.memberIdsToRemove,
                  ])
                : onMemberSelect([
                    recipient.memberId,
                    ...recipient.memberIdsToAdd,
                  ])
            }
          >
            <Member recipient={recipient} />
            <div>{relationships}</div>
          </Checkbox>
        </div>,
      );

      return memo;
    },
    [],
  );

  return (
    <div>
      <div className={styles.listHeader}>
        <SearchInput
          size="large"
          className={styles.search}
          placeholder={intl.formatMessage({
            id: 'messaging.ManageMembersModal.search',
          })}
          value={search}
          onChange={value => setSearch(value)}
        />

        <MeritBadgeDropdown
          selectedMeritBadges={selectedMeritBadges}
          setSelectedMeritBadges={setSelectedMeritBadges}
          counselingBadges={counselingBadges}
        />
      </div>
      <div className={styles.list}>{recipientList}</div>
    </div>
  );
};

export default MBCYouthsMembersList;
